import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useBack, useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { TEAL, AMBER, BORD, CARD, SUNK, DARK, MUTED, DIM, TEXT } from '../stockShared';
import { field } from '../numberFormat';

// Admin → Cost & Price (the director, 3 Nov 2026; migration 092): what each product
// costs and earns, what the year costs, what an hour on each machine costs, and which
// prices are still missing. Every number is worked out on the server, so this page and
// the calculation cannot disagree.

const money = (v, d = 2) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: d, maximumFractionDigits: d }));
const whole = (v) => money(v, 0);
const shown = (v) => (v === null || v === undefined ? '' : field(Number(v)));
const say = (err, fallback) => err.response?.data?.error || fallback;

const box = { background: CARD, border: `1px solid ${BORD}`, borderRadius: 10, padding: 16, display: 'grid', gap: 12, minWidth: 0 };
const scroll = { overflowX: 'auto', border: `1px solid ${BORD}`, borderRadius: 8, background: SUNK };
const h2 = { fontSize: 15, fontWeight: 700, margin: 0, color: TEXT };
const note = { fontSize: 12.5, lineHeight: 1.5, color: DIM, margin: 0, maxWidth: 780 };
const th = (right) => ({ padding: '8px 10px', textAlign: right ? 'right' : 'left', fontSize: 10.5, letterSpacing: '.07em', textTransform: 'uppercase', color: MUTED, whiteSpace: 'nowrap', fontWeight: 700 });
const td = { padding: '7px 10px', borderTop: `1px solid ${BORD}`, fontSize: 13, verticalAlign: 'middle', color: TEXT };
const tdr = { ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const input = (w, right = true) => ({ width: w, background: DARK, color: TEXT, border: `1px solid ${BORD}`, borderRadius: 6, padding: '5px 7px', fontSize: 13, textAlign: right ? 'right' : 'left', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box' });
const primary = (on = true) => ({ background: on ? TEAL : BORD, color: on ? '#06221f' : DIM, border: 'none', borderRadius: 7, padding: '7px 13px', fontWeight: 700, fontSize: 13, cursor: on ? 'pointer' : 'default', fontFamily: 'inherit' });
const quiet = { background: 'none', color: DIM, border: `1px solid ${BORD}`, borderRadius: 7, padding: '6px 11px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' };

const YEAR_ROWS = [
  ['rent_year', 'Rent', 'A year, without VAT'],
  ['waste_cleaning_year', 'Waste and cleaning', 'A year, without VAT'],
  ['electricity_year', 'Electricity', 'A year, without VAT'],
  ['electricity_kwh_year', 'Electricity used', 'kWh a year, from your meters'],
  ['repairs_year', 'Repairs, normal year', 'Machines and moulds'],
  ['repairs_one_off', 'One-off repairs, this year', 'Shown, not charged to products'],
  ['other_year', 'Other costs', 'Insurance, accounting, bank, phones, office, vehicles'],
  ['interest_year', 'Interest on loans', 'A year'],
];
const ASSUME_ROWS = [
  ['efficiency_pct', 'Efficiency, %', 'Running time against perfect running. A placeholder until the floor records real stops.'],
  ['regrind_credit_pct', 'Regrind value, %', 'What a runner is still worth, as a share of new material.'],
  ['working_days_year', 'Working days a year', 'Scales the days on the board up to a year.'],
  ['shift_hours', 'Hours in a shift', 'One shift is the least a machine\'s own costs are spread over.'],
  ['default_target_margin_pct', 'Default target margin, %', 'For products with no margin of their own.'],
  ['default_min_margin_pct', 'Default minimum margin, %', 'Below this the bell will ring.'],
  ['purge_kg_per_change', 'Purge and start-up, kg', 'Lost at each mould change. A placeholder until the floor records real purges.'],
];
const ROLE_WORD = { director: 'Director', setter: 'Setter', office: 'Office' };
const KIND_WORD = { net_month: 'Net a month', minimum_wage: 'Minimum wage', year: 'Yearly cost' };

// A small ⓘ that opens a panel with the sum behind a number. Opens on click, so it works
// on a phone; the panel is fixed to the screen so a scrolling table cannot clip it, and
// sits in the page body so a click inside it never reaches a label around the button.
function Info({ label, text, id, openId, setOpenId }) {
  const open = openId === id;
  const btn = useRef(null), panel = useRef(null);
  const [pos, setPos] = useState(null);
  const close = useCallback(() => setOpenId(null), [setOpenId]);

  useLayoutEffect(() => {
    if (!open || !btn.current) { setPos(null); return; }
    const r = btn.current.getBoundingClientRect(), vw = window.innerWidth, vh = window.innerHeight;
    const w = Math.min(340, vw - 24), h = panel.current ? panel.current.offsetHeight : 0;
    const below = r.bottom + 6, top = below + h > vh - 12 ? Math.max(12, r.top - h - 6) : below;
    setPos({ w, top, left: Math.min(Math.max(12, r.left - 8), vw - w - 12) });
  }, [open, text]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!(btn.current && btn.current.contains(e.target)) && !(panel.current && panel.current.contains(e.target))) close(); };
    const key = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  if (!text) return null;
  return (
    <>
      <button ref={btn} type="button" aria-label={`Where ${label} comes from`} aria-expanded={open}
        style={{ background: 'none', border: 'none', padding: '2px 4px', margin: '0 0 0 2px', cursor: 'pointer', color: open ? TEAL : MUTED, verticalAlign: 'middle', lineHeight: 1 }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenId(open ? null : id); }}>
        <i className="fa-solid fa-circle-info" style={{ fontSize: 11 }} />
      </button>
      {open && createPortal(
        <div ref={panel} role="note" style={{ position: 'fixed', zIndex: 2000, top: pos ? pos.top : 0, left: pos ? pos.left : 0, width: pos ? pos.w : Math.min(340, window.innerWidth - 24), visibility: pos ? 'visible' : 'hidden', boxSizing: 'border-box', background: CARD, border: `1px solid ${BORD}`, borderRadius: 8, padding: '10px 12px', fontSize: 12.5, lineHeight: 1.5, color: TEXT, fontWeight: 400, textTransform: 'none', letterSpacing: 0, textAlign: 'left', whiteSpace: 'normal', boxShadow: '0 6px 20px rgba(0,0,0,.35)' }}>{text}</div>,
        document.body,
      )}
    </>
  );
}

const dayShown = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
// Who typed a figure and when, from the history; the hint is already on screen.
const typedWhy = (t) => (t && t.at ? `Typed here. Last changed${t.by ? ` by ${t.by}` : ''} on ${dayShown(t.at)}.` : 'Typed here. Not changed since it was first set.');

function Rates({ phone }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [people, setPeople] = useState([]);
  const [peopleDirty, setPeopleDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [machines, setMachines] = useState({});
  const [openId, setOpenId] = useState(null);

  const take = useCallback((d) => {
    setData(d);
    setForm(Object.fromEntries([...YEAR_ROWS, ...ASSUME_ROWS].map(([k]) => [k, shown(d.settings[k])]).concat([['student_net_hour', shown(d.settings.student_net_hour)]])));
    setPeople((d.settings.people || []).map(p => ({ name: p.name, role: p.role, changes: !!p.changes_moulds, kind: p.pay.kind, amount: shown(p.pay.amount) })));
    setPeopleDirty(false);
    setMachines({});
  }, []);

  useEffect(() => {
    api.get('/api/v1/costing/rates').then(r => take(r.data)).catch(err => setError(say(err, 'The rates could not be read just now. Try again.')));
  }, [take]);

  const dirtyKeys = useMemo(() => {
    if (!data) return [];
    return Object.keys(form).filter(k => form[k] !== shown(data.settings[k]));
  }, [form, data]);
  const dirty = dirtyKeys.length > 0 || peopleDirty;

  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const body = Object.fromEntries(dirtyKeys.map(k => [k, form[k]]));
      if (peopleDirty) body.people = people.map(p => ({ name: p.name, role: p.role, ...(p.changes ? { changes_moulds: true } : {}), pay: p.kind === 'minimum_wage' ? { kind: p.kind } : { kind: p.kind, amount: p.amount } }));
      const { data: d } = await api.put('/api/v1/costing/rates', body);
      take(d);
      toast.success('Saved. Every rate below is worked out again.');
    } catch (err) {
      toast.error(say(err, 'It could not be saved just now. Your typing is still here; try again.'));
    } finally {
      setSaving(false);
    }
  }
  const person = (i, patch) => { setPeople(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p))); setPeopleDirty(true); };
  const costOf = (i) => { const p = people[i], was = data.people[i]; return was && was.pay.kind === p.kind && shown(was.pay.amount) === p.amount ? was : null; };

  const machineValue = (m, k) => { const e = machines[m.machine_id]; if (e && k in e) return e[k]; const v = m[k]; return k === 'value_kind' ? (v || 'paid') : (v === null || v === undefined ? '' : (k === 'bought_year' ? String(v) : shown(v))); };
  const setMachine = (id, k, v) => setMachines(s => ({ ...s, [id]: { ...s[id], [k]: v } }));
  async function saveMachine(m) {
    const e = { value_eur: machineValue(m, 'value_eur'), value_kind: machineValue(m, 'value_kind'), bought_year: machineValue(m, 'bought_year'), life_years: machineValue(m, 'life_years') || '10' };
    try {
      const { data: d } = await api.put(`/api/v1/costing/machines/${m.machine_id}`, e);
      take(d);
      toast.success(`${m.name} saved.`);
    } catch (err) {
      toast.error(say(err, 'It could not be saved just now. Try again.'));
    }
  }

  if (error) return <p style={{ ...note, color: '#fca5a5' }}>{error}</p>;
  if (!data) return <p style={note}>Reading the rates…</p>;
  const b = data.board, stat = (label, value, sub, why, id) => (
    <div style={{ background: SUNK, borderRadius: 8, padding: '10px 13px', display: 'grid', gap: 1 }}>
      <div style={{ fontSize: 12, color: DIM }}>{label}<Info label={label} text={why} id={id} openId={openId} setOpenId={setOpenId} /></div>
      <div style={{ fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: MUTED }}>{sub}</div>}
    </div>
  );
  const info = (id, label, text) => <Info label={label} text={text} id={id} openId={openId} setOpenId={setOpenId} />;
  const perHourOf = { rent_year: data.per_hour.rent, repairs_year: data.per_hour.repairs };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {data.needs.length > 0 && (
        <div style={{ background: '#38290a', borderRadius: 10, padding: '12px 14px', display: 'grid', gap: 6 }}>
          <div style={{ fontWeight: 700, color: AMBER, fontSize: 13.5 }}>Still needed for the rates to be complete</div>
          {data.needs.map(n => <div key={n.what} style={{ fontSize: 13, color: TEXT }}><b>{n.what}:</b> {n.says}</div>)}
        </div>
      )}

      <section style={box}>
        <h2 style={h2}>Prices and rates</h2>
        <p style={note}>What each one costs, worked out from what you type below. Press ⓘ to see the sum.</p>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: '10px 28px' }}>
          {data.why.figures.map(f => (
            <div key={f.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto auto', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontSize: 13 }}>{f.label}</span>
              <b style={{ fontSize: 13, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{f.value === null ? '—' : `€ ${money(f.value, f.key === 'electricity' ? 3 : 2)}`}</b>
              <span style={{ fontSize: 11.5, color: MUTED, whiteSpace: 'nowrap', minWidth: phone ? 0 : 88 }}>{f.per}</span>
              {info(`fig:${f.key}`, f.label, f.why)}
            </div>
          ))}
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {stat('Hours a year, all machines', whole(data.hours.total), `${whole(data.hours.change_hours)} of them changing moulds`, data.why.hours, 'hours')}
        {stat('Costs a year, before material', `€ ${whole(data.yearly.total)}`, 'And students, by the hour they work', data.why.year_total, 'year')}
      </div>

      <section style={box}>
        <h2 style={h2}>What the year costs</h2>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: '10px 28px' }}>
          {YEAR_ROWS.map(([k, label, hint]) => (
            <label key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 104px', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>{label}{info(`typed:${k}`, label, typedWhy(data.typed[k]))}
                <span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>{hint}{perHourOf[k] !== undefined && perHourOf[k] !== null ? ` · € ${money(perHourOf[k])} an hour` : ''}</span>
              </span>
              <input aria-label={label} inputMode="decimal" style={input('100%')} value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
            </label>
          ))}
        </div>
      </section>

      <section style={box}>
        <h2 style={h2}>People{info('typed:people', 'People', typedWhy(data.typed.people))}</h2>
        <div style={scroll}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={th()}>Who</th><th style={th()}>Does</th><th style={th()}>Pay</th><th style={th()}>Changes moulds</th><th style={th(true)}>Euros</th><th style={th(true)}>Gross</th><th style={th(true)}>Company, month</th><th style={th(true)}>Company, year</th><th style={th(true)}>Per hour worked</th><th style={th()}></th></tr></thead>
            <tbody>
              {people.map((p, i) => {
                const c = costOf(i);
                return (
                  <tr key={i}>
                    <td style={td}><input aria-label="Who" style={input(120, false)} value={p.name} onChange={e => person(i, { name: e.target.value })} /></td>
                    <td style={td}><select aria-label="Does" style={input(96, false)} value={p.role} onChange={e => person(i, { role: e.target.value })}>{Object.entries(ROLE_WORD).map(([v, w]) => <option key={v} value={v}>{w}</option>)}</select></td>
                    <td style={td}><select aria-label="Pay" style={input(122, false)} value={p.kind} onChange={e => person(i, { kind: e.target.value })}>{Object.entries(KIND_WORD).map(([v, w]) => <option key={v} value={v}>{w}</option>)}</select></td>
                    <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" aria-label={`${p.name} changes moulds`} style={{ accentColor: TEAL }} checked={p.changes} onChange={e => person(i, { changes: e.target.checked })} /></td>
                    <td style={tdr}>{p.kind === 'minimum_wage' ? <span style={{ color: MUTED, whiteSpace: 'nowrap' }} title="Worked back from the legal minimum wage">{c ? `${money(c.net)} net` : 'legal minimum'}</span> : <input aria-label="Euros" inputMode="decimal" style={input(84)} value={p.amount} onChange={e => person(i, { amount: e.target.value })} />}</td>
                    <td style={tdr}>{c ? whole(c.gross) : '…'}</td>
                    <td style={tdr}>{c ? whole(c.month) : '…'}</td>
                    <td style={tdr}>{c ? whole(c.year) : '…'}</td>
                    <td style={tdr}><b>{c ? `€ ${money(c.per_hour)}` : '…'}</b>{c && info(`person:${i}`, `${p.name || 'this person'}, per hour worked`, data.why.people[i])}</td>
                    <td style={td}><button type="button" aria-label={`Remove ${p.name}`} title="Remove" style={{ ...quiet, padding: '3px 8px' }} onClick={() => { setPeople(ps => ps.filter((_, j) => j !== i)); setPeopleDirty(true); }}>×</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
          <button type="button" style={quiet} onClick={() => { setPeople(ps => [...ps, { name: '', role: 'setter', changes: false, kind: 'net_month', amount: '' }]); setPeopleDirty(true); }}>+ Add a person</button>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>Student, net an hour
            <input aria-label="Student, net an hour" inputMode="decimal" style={input(70)} value={form.student_net_hour ?? ''} onChange={e => setForm(f => ({ ...f, student_net_hour: e.target.value }))} />
          </label>
          <span style={{ fontSize: 13 }}>costs the company <b>{data.student.per_hour === null ? '—' : `€ ${money(data.student.per_hour)}`}</b> an hour</span>
        </div>
        {people.some((_, i) => !costOf(i)) && <p style={{ ...note, color: AMBER }}>Press Save changes to work out the figures for the pay you changed.</p>}
        <p style={note}>Worked out with the {data.payroll.year} rates, including holiday and winter allowance, meals and commuting, over about {whole(data.payroll.hours_worked)} hours worked. Child allowances aren't counted, so your payroll may differ a little; "Yearly cost" takes your own figure instead. Directors and office go on every machine hour. Whoever changes moulds: that time goes on the orders, and the rest of their year on every machine hour. Students are charged to products by the time they spend.</p>
      </section>

      <section style={box}>
        <h2 style={h2}>An hour on each machine</h2>
        <div style={scroll}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={th()}>Machine</th><th style={th(true)}>Worth, €</th><th style={th()}>That is</th><th style={th(true)}>Bought</th><th style={th(true)}>Years</th><th style={th(true)}>Running h a year</th><th style={th(true)}>Machine</th><th style={th(true)}>Rent</th><th style={th(true)}>Electricity</th><th style={th(true)}>Repairs</th><th style={th(true)}>Machine hour</th><th style={th(true)}>With overheads</th><th style={th()}></th></tr></thead>
            <tbody>
              {data.machines.map(m => (
                <tr key={m.machine_id}>
                  <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{m.name}</td>
                  <td style={tdr}><input aria-label={`${m.name}, worth`} inputMode="decimal" style={input(84)} value={machineValue(m, 'value_eur')} onChange={e => setMachine(m.machine_id, 'value_eur', e.target.value)} />{info(`worth:${m.machine_id}`, `${m.name}, worth`, typedWhy(m.typed))}</td>
                  <td style={td}><select aria-label={`${m.name}, that is`} style={input(112, false)} value={machineValue(m, 'value_kind')} onChange={e => setMachine(m.machine_id, 'value_kind', e.target.value)}><option value="paid">What I paid</option><option value="replacement">To replace it</option></select></td>
                  <td style={tdr}><input aria-label={`${m.name}, bought`} inputMode="numeric" style={input(58)} value={machineValue(m, 'bought_year')} onChange={e => setMachine(m.machine_id, 'bought_year', e.target.value)} /></td>
                  <td style={tdr}><input aria-label={`${m.name}, years`} inputMode="decimal" style={input(46)} value={machineValue(m, 'life_years')} onChange={e => setMachine(m.machine_id, 'life_years', e.target.value)} /></td>
                  <td style={tdr}>{whole(m.running_year)}</td>
                  <td style={tdr}>{money(m.depreciation)}</td>
                  <td style={tdr}>{money(m.rent)}</td>
                  <td style={tdr}>{money(m.electricity)}</td>
                  <td style={tdr}>{money(m.repairs)}</td>
                  <td style={tdr}><b>{money(m.rate)}</b>{info(`machine:${m.machine_id}`, `${m.name}, machine hour`, data.why.machines[m.machine_id])}</td>
                  <td style={tdr}>{money(m.with_overheads)}</td>
                  <td style={td}>{machines[m.machine_id] && <button type="button" style={{ ...primary(), padding: '4px 10px' }} onClick={() => saveMachine(m)}>Save</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={note}>Running hours are pieces × cycle ÷ cavities from the board, {b.from ? `${b.from} to ${b.to}, ${b.days_on_board} working days scaled to a year` : 'once it has runs'}. A written-off machine isn't free: it still has to be replaced one day, so it carries what replacing it would cost. A machine's own costs spread over its hours, but never fewer than one shift ({whole(data.hours.one_shift)} h), so a press that rarely runs doesn't make its parts look expensive. That leaves € {whole(data.yearly.idle_machines)} a year of machine value that no product carries.{data.yearly.one_off_repairs ? ` This year's one-off repairs of € ${whole(data.yearly.one_off_repairs)} are also left out of the rates.` : ''}{b.rows_without_cycle ? ` ${b.rows_without_cycle} runs on the board have no cycle time, so their hours are missing.` : ''}</p>
      </section>

      <section style={box}>
        <h2 style={h2}>How it is worked out</h2>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: '10px 28px' }}>
          {ASSUME_ROWS.map(([k, label, hint]) => (
            <label key={k} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 80px', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>{label}{info(`typed:${k}`, label, typedWhy(data.typed[k]))}<span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>{hint}</span></span>
              <input aria-label={label} inputMode="decimal" style={input('100%')} value={form[k] ?? ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
            </label>
          ))}
        </div>
      </section>

      {dirty && (
        <div style={{ position: 'sticky', bottom: phone ? 76 : 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" style={{ ...quiet, background: CARD }} onClick={() => take(data)}>Undo my typing</button>
          <button type="button" style={primary(!saving)} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      )}
    </div>
  );
}

const GROUP_WORD = { folding_box: 'Product box', transport_box: 'Transport box', bag: 'Bag', insert: 'Insert', pallet: 'Pallet', tape: 'Tape', foil: 'Foil', other: 'Other', bought: 'Bought', outside: 'Done outside' };

function PriceRow({ item, onSaved, focus }) {
  const { toast } = useToast();
  const [price, setPrice] = useState(shown(item.price));
  const [date, setDate] = useState(item.price_date || '');
  const [saving, setSaving] = useState(false);
  const dirty = price !== shown(item.price) || date !== (item.price_date || '');
  const undated = item.price !== null && item.price !== undefined && !item.price_date;
  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/api/v1/costing/prices/${item.kind}/${item.id}`, { price, price_date: date || undefined });
      onSaved({ ...item, price: data.price, price_date: data.price_date });
      setDate(data.price_date || '');
      toast.success(`${item.name} saved.`);
    } catch (err) {
      toast.error(say(err, 'It could not be saved just now. Try again.'));
    } finally {
      setSaving(false);
    }
  }
  const used = item.left_out ? 'left out' : item.on_every_pack ? 'every pack' : item.used_on;
  const here = focus === `${item.kind}-${item.id}`;
  return (
    <tr id={`price-${item.kind}-${item.id}`} style={here ? { background: `${TEAL}22` } : undefined}>
      <td style={{ ...td, minWidth: 190 }}>{item.name}{item.supplier && <span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>{item.supplier}</span>}</td>
      <td style={{ ...td, color: DIM, whiteSpace: 'nowrap' }}>{GROUP_WORD[item.group] || item.group || ''}</td>
      <td style={{ ...tdr, color: item.used_on || item.on_every_pack ? TEXT : MUTED }}>{used}</td>
      <td style={tdr}><input aria-label={`${item.name}, price`} inputMode="decimal" style={input(84)} value={price} onChange={e => setPrice(e.target.value)} /></td>
      <td style={{ ...td, color: MUTED, whiteSpace: 'nowrap' }}>per {item.unit}</td>
      <td style={td}><input aria-label={`${item.name}, date`} type="date" style={input(138, false)} value={date} onChange={e => setDate(e.target.value)} /></td>
      <td style={{ ...td, whiteSpace: 'nowrap', minWidth: 96 }}>
        {dirty || undated
          ? <button type="button" style={{ ...primary(!saving), padding: '4px 10px' }} disabled={saving} onClick={save}>{!dirty && undated ? 'Confirm' : 'Save'}</button>
          : (item.price !== null && item.price !== undefined ? <span style={{ color: TEAL, fontSize: 12.5 }}>Done</span> : null)}
      </td>
    </tr>
  );
}

function PriceGroup({ title, hint, items, onSaved, focus }) {
  if (!items.length) return null;
  return (
    <section style={box}>
      <div><h2 style={h2}>{title} <span style={{ color: MUTED, fontWeight: 500 }}>{items.length}</span></h2>{hint && <p style={{ ...note, marginTop: 3 }}>{hint}</p>}</div>
      <div style={scroll}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr><th style={th()}>Item</th><th style={th()}>Kind</th><th style={th(true)}>Used on</th><th style={th(true)}>Price, €</th><th style={th()}></th><th style={th()}>Invoice date</th><th style={th()}></th></tr></thead>
          <tbody>{items.map(i => <PriceRow key={`${i.kind}-${i.id}`} item={i} onSaved={onSaved} focus={focus} />)}</tbody>
        </table>
      </div>
    </section>
  );
}

function Prices({ onCount, focus, refresh }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(focus ? 'all' : 'todo');

  useEffect(() => {
    api.get('/api/v1/costing/prices').then(r => setData(r.data)).catch(err => setError(say(err, 'The prices could not be read just now. Try again.')));
  }, [refresh]);
  // Opened from a cost line: show that item, with everything around it.
  useEffect(() => { if (focus) setView('all'); }, [focus]);
  useEffect(() => {
    if (!data || !focus) return;
    const row = document.getElementById(`price-${focus}`);
    if (row) row.scrollIntoView({ block: 'center' });
  }, [data, focus, view]);
  const isUsed = (i) => !i.left_out && (i.used_on > 0 || i.on_every_pack);
  const todo = useCallback((i) => isUsed(i) && (i.price === null || i.price === undefined || !i.price_date), []);
  const counts = useMemo(() => {
    if (!data) return null;
    const all = [...data.materials, ...data.packaging, ...data.bought];
    return { all: all.length, missing: all.filter(i => isUsed(i) && (i.price === null || i.price === undefined)).length, undated: all.filter(i => isUsed(i) && i.price !== null && i.price !== undefined && !i.price_date).length };
  }, [data]);
  useEffect(() => { if (counts) onCount(counts.missing + counts.undated); }, [counts, onCount]);

  const saved = (next) => {
    const key = { material: 'materials', packaging: 'packaging', bought: 'bought' }[next.kind];
    setData(d => ({ ...d, [key]: d[key].map(i => (i.id === next.id ? next : i)) }));
  };
  if (error) return <p style={{ ...note, color: '#fca5a5' }}>{error}</p>;
  if (!data) return <p style={note}>Reading the prices…</p>;
  const pick = (list) => (view === 'todo' ? list.filter(todo) : list);
  const tab = (id, label) => <button type="button" onClick={() => setView(id)} aria-pressed={view === id} style={{ ...quiet, background: view === id ? TEAL : 'none', color: view === id ? '#06221f' : DIM, borderColor: view === id ? TEAL : BORD, fontWeight: view === id ? 700 : 400 }}>{label}</button>;
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {tab('todo', 'To do')}
        {tab('all', 'Everything')}
        <span style={{ fontSize: 13, color: DIM }}>{counts.missing} prices missing, {counts.undated} to confirm with a date</span>
      </div>
      <p style={note}>A missing price never counts as zero: a product that needs it will say it is incomplete. Type the price from your latest invoice, without VAT. A date left empty means today.</p>
      <PriceGroup title="Materials" hint="Per kg, with inbound freight if you can." items={pick(data.materials)} onSaved={saved} focus={focus} />
      <PriceGroup title="Packaging" hint="Per piece; one roll for tape and foil." items={pick(data.packaging)} onSaved={saved} focus={focus} />
      <PriceGroup title="Bought parts and outside work" hint="Per the unit they are kept in." items={pick(data.bought)} onSaved={saved} focus={focus} />
      {view === 'todo' && counts.missing + counts.undated === 0 && <p style={{ ...note, color: TEAL }}>Every price in use is filled in and dated.</p>}
    </div>
  );
}

function LineSwitch({ on, label, disabled, onClick }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} title={on ? 'Counted in the cost' : 'Left out of the cost'} disabled={disabled} onClick={onClick}
      style={{ width: 32, height: 18, borderRadius: 9, border: 'none', padding: 2, background: on ? TEAL : BORD, cursor: disabled ? 'default' : 'pointer', display: 'inline-flex', justifyContent: on ? 'flex-end' : 'flex-start', opacity: disabled ? 0.6 : 1, verticalAlign: 'middle' }}>
      <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff' }} />
    </button>
  );
}

const STANDING = {
  on_target: ['On target', '#12301d', '#5ee08a'],
  between: ['Above minimum', '#38290a', AMBER],
  below_minimum: ['Below minimum', '#3a1714', '#fb8a80'],
  below_variable: ['Below variable cost', '#b42318', '#fff'],
  no_margin_set: ['No margin set', SUNK, DIM],
};
const chip = (word, bg, fg) => <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: bg, color: fg, whiteSpace: 'nowrap', border: bg === SUNK ? `1px solid ${BORD}` : 'none' }}>{word}</span>;
const standingChip = (r) => {
  if (!r.complete) return chip(`${r.gap_count ?? r.gaps.length} missing`, SUNK, AMBER);
  const s = STANDING[r.standing] || STANDING.no_margin_set;
  return chip(s[0], s[1], s[2]);
};
const costWord = (r) => (r.cost === null ? '—' : `${r.complete ? '' : 'at least '}${money(r.cost, 4)}`);
const percent = (m) => (m === null || m === undefined ? '—' : `${money(m * 100, 1)} %`);

function ProductList({ visible, onOpen, onTab, refresh, phone }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [find, setFind] = useState('');
  const [view, setView] = useState('all');
  const [sort, setSort] = useState('margin');

  useEffect(() => {
    if (!visible) return undefined;
    let live = true;
    api.get('/api/v1/costing/products').then(r => { if (live) { setData(r.data); setError(null); } }).catch(err => { if (live) setError(say(err, 'The products could not be read just now. Try again.')); });
    return () => { live = false; };
  }, [visible, refresh]);

  if (error) return <p style={{ ...note, color: '#fca5a5' }}>{error}</p>;
  if (!data) return <p style={note}>Working out every product…</p>;
  const word = find.trim().toLowerCase();
  const ready = data.rows.filter(r => r.complete).length;
  const rows = data.rows
    .filter(r => (view === 'all' ? true : view === 'ready' ? r.complete : !r.complete))
    .filter(r => !word || r.name.toLowerCase().includes(word) || String(r.number || '').toLowerCase().includes(word))
    .sort((a, b) => {
      if (a.complete !== b.complete) return a.complete ? -1 : 1;
      if (sort === 'name') return a.name.localeCompare(b.name);
      const key = sort === 'hour' ? 'left_per_machine_hour' : 'margin';
      if (a.complete) return (a[key] ?? Infinity) - (b[key] ?? Infinity);
      return (a.gap_count - b.gap_count) || a.name.localeCompare(b.name);
    });
  const pick = (value, now, set, label) => <button type="button" key={value} onClick={() => set(value)} aria-pressed={now === value} style={{ ...quiet, background: now === value ? TEAL : 'none', color: now === value ? '#06221f' : DIM, borderColor: now === value ? TEAL : BORD, fontWeight: now === value ? 700 : 400 }}>{label}</button>;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {data.rates_needed > 0 && (
        <div style={{ background: '#38290a', borderRadius: 10, padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13.5 }}><b style={{ color: AMBER }}>Fill in the Rates tab first.</b> Until then, costs leave out machine hours, people and overheads.</span>
          <button type="button" style={{ ...primary(), marginLeft: 'auto' }} onClick={() => onTab('rates')}>Open Rates</button>
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input aria-label="Find a product" placeholder="Find a product…" style={{ ...input(phone ? '100%' : 220, false) }} value={find} onChange={e => setFind(e.target.value)} />
        {pick('all', view, setView, `All ${data.rows.length}`)}
        {pick('ready', view, setView, `Complete ${ready}`)}
        {pick('todo', view, setView, `Something missing ${data.rows.length - ready}`)}
        <span style={{ marginLeft: phone ? 0 : 'auto', fontSize: 12.5, color: MUTED }}>Lowest first:</span>
        {pick('margin', sort, setSort, 'Margin')}
        {pick('hour', sort, setSort, 'Per machine hour')}
        {pick('name', sort, setSort, 'Name')}
      </div>
      <div style={scroll}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr><th style={th()}>Product or part</th><th style={th(true)}>Cost</th><th style={th(true)}>List price</th><th style={th(true)}>Margin</th><th style={th(true)}>Per machine hour</th><th style={th()}>Standing</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={`${r.kind}-${r.id}`} onClick={() => onOpen(r.kind, r.id)} style={{ cursor: 'pointer' }}>
                <td style={{ ...td, minWidth: 220 }}><button type="button" onClick={(e) => { e.stopPropagation(); onOpen(r.kind, r.id); }} style={{ background: 'none', border: 'none', color: TEXT, padding: 0, font: 'inherit', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>{r.name}</button>{r.number && <span style={{ display: 'block', fontSize: 11, color: MUTED }}>{r.number}</span>}{(r.left_out || []).some(l => l.scope === 'product') && <span style={{ display: 'block', fontSize: 11, color: AMBER }}>Leaves out {r.left_out.filter(l => l.scope === 'product').map(l => l.name).join(', ')}</span>}</td>
                <td style={tdr}>{costWord(r)}</td>
                <td style={tdr}>{r.list_price === null ? <span style={{ color: MUTED }}>none</span> : `${money(r.list_price, 4)}${r.unit === 'kg' ? ' a kg' : ''}`}</td>
                <td style={tdr}>{r.complete ? percent(r.margin) : <span style={{ color: MUTED }}>—</span>}</td>
                <td style={tdr}>{r.complete && r.left_per_machine_hour !== null ? `€ ${money(r.left_per_machine_hour, 0)}` : <span style={{ color: MUTED }}>—</span>}</td>
                <td style={td}>{standingChip(r)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} style={{ ...td, color: DIM }}>Nothing matches. Clear the search, or choose All.</td></tr>}
          </tbody>
        </table>
      </div>
      <p style={note}>Each is worked out at its usual run on the board, or its usual order. "Per machine hour" is what is left of the price after what it takes to make, for each hour it holds a machine: a fair way to compare products competing for the same press. {data.payment.says}{(data.left_out_everywhere || []).length > 0 && ` Left out of every product: ${data.left_out_everywhere.join(', ')}.`}</p>
    </div>
  );
}

function ProductPage({ kind, id, onBack, onTab, onChanged, phone }) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [typed, setTyped] = useState('');
  const [size, setSize] = useState(null);
  const [target, setTarget] = useState('');
  const [minimum, setMinimum] = useState('');
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(null);
  const [turning, setTurning] = useState(null);
  const [unfolded, setUnfolded] = useState({});

  const read = useCallback(() => {
    const q = size ? `?qty=${encodeURIComponent(size)}` : '';
    return api.get(`/api/v1/costing/products/${kind}/${id}${q}`).then(r => {
      setData(r.data); setError(null);
      setTarget(shown(r.data.target_margin_pct)); setMinimum(shown(r.data.min_margin_pct));
    }).catch(err => setError(say(err, 'This product could not be read just now. Try again.')));
  }, [kind, id, size]);
  useEffect(() => { read(); }, [read]);

  const go = (fix) => {
    if (!fix) return;
    if (fix.to === 'prices' || fix.to === 'rates') return onTab(fix.to, fix.item);
    if (fix.to === 'build') return navigate(kind === 'product' ? `/stock/build/${id}` : (data.item_id ? `/stock/build/part/${data.item_id}` : '/stock'));
    if (fix.to === 'parts') return navigate(fix.id ? `/admin/parts/${fix.id}` : '/parts');
    if (fix.to === 'moulds') return navigate('/admin/moulds');
    if (fix.to === 'packaging') return navigate(fix.id ? `/admin/packaging?container=${fix.id}` : '/admin/packaging');
    return navigate('/stock');
  };
  const fixWord = { prices: 'Prices', rates: 'Rates', build: 'Build steps', parts: 'Part', moulds: 'Moulds', packaging: 'Packaging', stock: 'Stock' };

  async function countLine(r, out, scope) {
    setTurning(r.key);
    try {
      await api.put('/api/v1/costing/left-out', { key: r.key, name: r.name, out, scope, root: `${kind}:${id}`, root_name: data.name });
      setAsking(null);
      await read();
      onChanged();
      toast.success(out ? `${r.name} left out of ${scope === 'all' ? 'every product' : data.name}.` : `${r.name} counts again.`);
    } catch (err) {
      toast.error(say(err, 'It could not be changed just now. Try again.'));
    } finally {
      setTurning(null);
    }
  }
  const fold = (b) => {
    const opening = !unfolded[b.key];
    setUnfolded(u => ({ ...u, [b.key]: opening }));
    if (!opening && b.rows.some(r => r.key === asking)) setAsking(null);
  };
  // Counted: ask how far. Left out of this product: back on at once. Left out of
  // every product: that changes all of them, so it asks first.
  const flip = (r) => { if (r.left_out === 'product') countLine(r, false, 'product'); else setAsking(asking === r.key ? null : r.key); };

  async function saveMargins() {
    setSaving(true);
    try {
      await api.put(`/api/v1/costing/margins/${kind}/${id}`, { target_margin_pct: target, min_margin_pct: minimum });
      await read();
      onChanged();
      toast.success('Margins saved for this product.');
    } catch (err) {
      toast.error(say(err, 'The margins could not be saved just now. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  const back = <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: 'inherit', justifySelf: 'start' }}>‹ All products</button>;
  if (error) return <div style={{ display: 'grid', gap: 10 }}>{back}<p style={{ ...note, color: '#fca5a5' }}>{error}</p></div>;
  if (!data) return <div style={{ display: 'grid', gap: 10 }}>{back}<p style={note}>Working it out…</p></div>;
  const dirty = target !== shown(data.target_margin_pct) || minimum !== shown(data.min_margin_pct);
  const stat = (label, value, sub) => (
    <div style={{ background: SUNK, borderRadius: 8, padding: '10px 13px', display: 'grid', gap: 1, alignContent: 'start' }}>
      <div style={{ fontSize: 12, color: DIM }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: MUTED }}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {back}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <div><h2 style={{ fontSize: 19, fontWeight: 800, margin: 0 }}>{data.name}</h2>{data.number && <div style={{ fontSize: 12, color: MUTED }}>{data.number}</div>}</div>
        {standingChip(data)}
        <form onSubmit={(e) => { e.preventDefault(); setSize(typed || null); }} style={{ marginLeft: phone ? 0 : 'auto', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: DIM }} htmlFor="cost-size">Order size</label>
          <input id="cost-size" inputMode="numeric" placeholder={whole(data.usual_order)} style={input(92)} value={typed} onChange={e => setTyped(e.target.value)} />
          <button type="submit" style={quiet}>Work it out</button>
          {size && <button type="button" style={quiet} onClick={() => { setTyped(''); setSize(null); }}>Usual</button>}
        </form>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr 1fr' : 'repeat(5, 1fr)', gap: 10 }}>
        {stat(data.complete ? 'Full cost' : 'Full cost, at least', data.cost === null ? '—' : `€ ${money(data.cost, 4)}`, size ? `at ${whole(data.qty)} pieces` : 'at its usual run')}
        {stat('List price', data.list_price === null ? 'None' : `€ ${money(data.list_price, 4)}`, data.unit === 'kg' ? `a kg: € ${money(data.price, 4)} a piece` : null)}
        {stat(data.complete ? 'Margin' : 'Margin, at most', percent(data.margin), data.net_price !== data.price ? 'after the 2 %' : null)}
        {stat(data.complete ? 'Per machine hour' : 'Per machine hour, at most', data.left_per_machine_hour === null ? '—' : `€ ${money(data.left_per_machine_hour, 0)}`, 'Left after what it takes to make')}
        {stat(data.complete ? 'Variable cost' : 'Variable cost, at least', `€ ${money(data.variable, 4)}`, 'Below this, every piece loses cash')}
      </div>

      {data.gaps.length > 0 && (
        <section style={{ ...box, background: '#2a2008', borderColor: '#5a4312' }}>
          <h2 style={{ ...h2, color: AMBER }}>{data.gaps.length} {data.gaps.length === 1 ? 'thing is' : 'things are'} missing, so this cost is not complete</h2>
          <div style={{ display: 'grid', gap: 7 }}>
            {data.gaps.map((g, n) => (
              <div key={n} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', fontSize: 13 }}>
                <span style={{ minWidth: 0, flex: '1 1 260px' }}>{g.says}</span>
                {g.fix && <button type="button" style={quiet} onClick={() => go(g.fix)}>Open {fixWord[g.fix.to] || 'it'}</button>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section style={box}>
        <h2 style={h2}>What it costs, for one piece</h2>
        <div style={scroll}>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: phone ? 514 : 760 }}>
            <colgroup><col style={{ width: phone ? 120 : 240 }} /><col style={phone ? { width: 90 } : undefined} /><col style={{ width: phone ? 110 : 130 }} /><col style={{ width: phone ? 60 : 70 }} /><col style={{ width: phone ? 64 : 72 }} /><col style={{ width: phone ? 70 : 76 }} /></colgroup>
            <thead><tr><th style={th()}>Line</th><th style={th()}></th><th style={th(true)}>€ a piece</th><th style={th(true)}>Share</th><th style={th()}>Counts</th><th style={th()}></th></tr></thead>
            <tbody>
              {data.buckets.map(b => (
                <Fragment key={b.key}>
                  <tr style={{ background: CARD, cursor: 'pointer' }} onClick={() => fold(b)}>
                    <td style={{ ...td, fontWeight: 700 }} colSpan={2}>
                      <button type="button" aria-expanded={!!unfolded[b.key]} aria-label={`${unfolded[b.key] ? 'Hide' : 'Show'} ${b.label}`} onClick={(e) => { e.stopPropagation(); fold(b); }}
                        style={{ background: 'none', border: 'none', padding: 0, color: DIM, cursor: 'pointer', width: 18, flex: 'none' }}>
                        <i className={`fa-solid fa-chevron-${unfolded[b.key] ? 'down' : 'right'}`} style={{ fontSize: 10 }} />
                      </button>
                      {b.label}
                      {!unfolded[b.key] && (() => {
                        const missing = b.rows.filter(r => r.per_piece === null && !r.left_out).length;
                        const out = b.rows.filter(r => r.left_out).length;
                        return <>
                          {missing > 0 && <span style={{ marginLeft: 8 }}>{chip(`${missing} missing`, SUNK, AMBER)}</span>}
                          {out > 0 && <span style={{ marginLeft: 8 }}>{chip(`${out} left out`, SUNK, DIM)}</span>}
                        </>;
                      })()}
                    </td>
                    <td style={{ ...tdr, fontWeight: 700 }}>{money(b.per_piece, 4)}</td>
                    <td style={{ ...tdr, color: DIM }}>{data.cost ? `${money(b.per_piece / data.cost * 100, 0)} %` : ''}</td>
                    <td style={td} colSpan={2}>{b.key === 'machine' && <button type="button" style={{ ...quiet, padding: '3px 8px' }} title="What an hour on each machine costs" onClick={(e) => { e.stopPropagation(); onTab('rates'); }}>Edit rates</button>}</td>
                  </tr>
                  {unfolded[b.key] && b.rows.map((r, n) => (
                    <Fragment key={r.key || n}>
                      <tr style={r.left_out ? { opacity: 0.6 } : undefined}>
                        {(() => {
                          const detail = r.left_out ? (r.left_out === 'all' ? 'Left out of every product' : 'Left out of this product only') : r.detail;
                          return phone
                            ? <td style={{ ...td, paddingLeft: 22 }} colSpan={2}>{r.name}<div style={{ color: DIM, fontSize: 12 }}>{detail}</div></td>
                            : <><td style={{ ...td, paddingLeft: 22 }}>{r.name}</td><td style={{ ...td, color: DIM, fontSize: 12 }}>{detail}</td></>;
                        })()}
                        <td style={{ ...tdr, color: r.per_piece === null && !r.left_out ? AMBER : TEXT, textDecoration: r.left_out ? 'line-through' : 'none' }}>{r.per_piece === null ? (r.left_out ? '—' : 'no figure') : money(r.per_piece, 4)}</td>
                        <td style={tdr}></td>
                        <td style={td}><LineSwitch on={!r.left_out} label={`Count ${r.name} in the cost`} disabled={turning === r.key} onClick={() => flip(r)} /></td>
                        <td style={td}>{r.fix && <button type="button" style={{ ...quiet, padding: '3px 8px' }} title={`Open ${fixWord[r.fix.to] || 'it'}`} onClick={() => go(r.fix)}>Edit</button>}</td>
                      </tr>
                      {asking === r.key && (
                        <tr style={{ background: SUNK }}>
                          <td style={{ ...td, paddingLeft: 22 }} colSpan={6}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 13 }}>
                              {r.left_out
                                ? <><span>Count {r.name} again on every product?</span><button type="button" style={primary(!turning)} disabled={!!turning} onClick={() => countLine(r, false, 'all')}>Count it again</button></>
                                : <><span>Leave {r.name} out of:</span>
                                  <button type="button" style={primary(!turning)} disabled={!!turning} onClick={() => countLine(r, true, 'all')}>Every product</button>
                                  <button type="button" style={quiet} disabled={!!turning} onClick={() => countLine(r, true, 'product')}>Only {data.name}</button></>}
                              <button type="button" style={quiet} onClick={() => setAsking(null)}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
              {(() => {
                const mainOn = data.main_on || [], madeOn = data.made_on || [], many = mainOn.length > 1;
                const one = mainOn.length === 1 && mainOn[0].machine ? mainOn[0] : null;
                const runsOf = (e) => (e && e.main.runs > 0 ? `${e.main.runs} of ${e.total_runs} runs on the board` : null);
                const mainRuns = !many && madeOn.length === 1 ? runsOf(madeOn[0]) : null;
                const full = data.complete ? 'Full cost' : 'Full cost, at least';
                return <>
                  <tr style={{ background: data.complete ? `${TEAL}1f` : '#2a2008' }}>
                    <td style={{ ...td, paddingTop: 14, paddingBottom: 14, fontSize: 15, fontWeight: 800, color: TEXT }} colSpan={2}>
                      {full}{one ? ` · Made on: ${one.machine}` : many ? ' · each piece on its main machine' : ''}
                      {mainRuns && <div style={{ fontSize: 12, fontWeight: 400, color: MUTED }}>{mainRuns}</div>}
                      {many && <div style={{ fontSize: 12, fontWeight: 400, color: DIM, lineHeight: 1.45 }}>{mainOn.map(m => `${m.part} on ${m.machine || 'no machine'}${runsOf(madeOn.find(e => e.part_id === m.part_id)) ? ` (${runsOf(madeOn.find(e => e.part_id === m.part_id))})` : ''}`).join(', ')}</div>}
                    </td>
                    <td style={{ ...tdr, paddingTop: 14, paddingBottom: 14, fontSize: phone ? 17 : 19, fontWeight: 800, color: data.complete ? TEAL : AMBER }}>€ {money(data.cost, 4)}</td>
                    <td style={{ ...tdr, paddingTop: 14, paddingBottom: 14 }} colSpan={3}></td>
                  </tr>
                  {madeOn.flatMap(e => e.others.map(o => (
                    <tr key={`${e.part_id}:${o.machine_id}`} style={{ background: o.complete ? `${TEAL}10` : '#211a07' }}>
                      <td style={td} colSpan={2}>
                        <div style={{ fontSize: 13.5, fontWeight: 700 }}>{many ? `${e.part}, made on: ${o.machine}` : `Made on: ${o.machine}`}</div>
                        {(o.runs > 0 || !o.listed) && <div style={{ fontSize: 12, color: MUTED }}>{[o.runs > 0 ? `${o.runs} of ${e.total_runs} runs on the board` : null, o.listed ? null : 'not on this part\'s machine list'].filter(Boolean).join(' · ')}</div>}
                        <div style={{ fontSize: 12, color: DIM, lineHeight: 1.45 }}>{o.why}</div>
                      </td>
                      <td style={{ ...tdr, fontSize: 15, fontWeight: 800, color: o.cost === null ? MUTED : o.complete ? TEAL : AMBER }}>
                        {o.cost !== null && !o.complete && <div style={{ fontSize: 11, fontWeight: 600 }}>at least</div>}
                        {o.cost === null ? '—' : `€ ${money(o.cost, 4)}`}
                      </td>
                      <td style={{ ...tdr, color: MUTED }} colSpan={3}>
                        {o.difference !== null && <div>{o.difference < 0 ? '−' : '+'}€ {money(Math.abs(o.difference), 4)}</div>}
                        {o.margin !== null && <div style={{ fontSize: 12 }}>{percent(o.margin)} margin</div>}
                      </td>
                    </tr>
                  )))}
                </>;
              })()}
            </tbody>
          </table>
        </div>
        {(data.made_on || []).length > 0 && <p style={note}>Each machine is worked out with the same cycle time, because a part has one cycle time in Parts. If it really runs faster on one press, its cost there will be lower than shown.</p>}
        <p style={note}>What you buy: € {money(data.buy, 4)}. What you do: € {money(data.does, 4)}. A product that is mostly bought-in parts earns its margin differently from one that is mostly your own work.</p>
        {(data.left_out || []).length > 0 && <p style={{ ...note, color: AMBER }}>This cost leaves out {data.left_out.map(l => l.name).join(', ')}. Switch a line back on to count it.</p>}
      </section>

      <section style={box}>
        <h2 style={h2}>Margin for this product</h2>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: '10px 28px' }}>
          <label style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 80px', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 13 }}>Target margin, %<span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>{data.target_price === null ? 'Set one here or as the default on Rates' : `Needs a price of € ${money(data.target_price, 4)}`}</span></span>
            <input aria-label="Target margin" inputMode="decimal" style={input('100%')} placeholder={shown(data.default_target_margin_pct)} value={target} onChange={e => setTarget(e.target.value)} />
          </label>
          <label style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 80px', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 13 }}>Minimum margin, %<span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>{data.minimum_price === null ? 'Below this the bell will ring' : `Needs a price of € ${money(data.minimum_price, 4)}`}</span></span>
            <input aria-label="Minimum margin" inputMode="decimal" style={input('100%')} placeholder={shown(data.default_min_margin_pct)} value={minimum} onChange={e => setMinimum(e.target.value)} />
          </label>
        </div>
        {dirty && <div><button type="button" style={primary(!saving)} disabled={saving} onClick={saveMargins}>{saving ? 'Saving…' : 'Save margins'}</button></div>}
        <p style={note}>Left empty, this product follows the default on the Rates tab. Saving a margin never changes the price.</p>
      </section>

      {(data.smallest_order || data.never_at_minimum || data.payment) && (
        <section style={box}>
          <h2 style={h2}>Good to know</h2>
          <div style={{ display: 'grid', gap: 6, fontSize: 13, lineHeight: 1.5 }}>
            {data.smallest_order && <div>Below about <b>{whole(data.smallest_order)} pieces</b>, one mould change pushes this under its minimum margin. That is the size a Minimum Quantity Surcharge starts from.</div>}
            {data.never_at_minimum && <div>At this price it stays under its minimum margin at any order size, so a bigger order won't fix it.</div>}
            {data.payment && <div style={{ color: DIM }}>{data.payment.says}</div>}
          </div>
        </section>
      )}
    </div>
  );
}

// What if (the director, 5 Nov 2026): change one or several figures and see every product's cost,
// margin and the price for its target margin, now and after. Nothing is saved: the server works the
// same costs out again on a changed copy and sends both back.
const CHANGES = [
  ['electricity_per_kwh', 'Electricity', '€ a kWh'],
  ['rent_year', 'Rent', '€ a year'],
  ['waste_cleaning_year', 'Waste and cleaning', '€ a year'],
  ['repairs_year', 'Repairs in a normal year', '€ a year'],
  ['other_year', 'Other costs', '€ a year'],
  ['interest_year', 'Interest', '€ a year'],
  ['student_net_hour', 'Student, net an hour', '€ an hour'],
  ['efficiency_pct', 'Efficiency', '%'],
];
const PERCENT_CHANGES = [
  ['materials_pct', 'Materials, all', 'Change in %, for example 10 or -5'],
  ['packaging_pct', 'Packaging, all', 'Change in %'],
  ['bought_pct', 'Bought parts and outside work, all', 'Change in %'],
  ['list_price_pct', 'Your list prices, all', 'Change in %'],
];
const signed = (d, digits) => `${d > 0 ? '+' : ''}${money(d, digits)}`;

function WhatIf({ visible, phone }) {
  const [rates, setRates] = useState(null);
  const [mats, setMats] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [pay, setPay] = useState({});
  const [own, setOwn] = useState([]);
  const [pick, setPick] = useState('');
  const [pickPrice, setPickPrice] = useState('');
  const [result, setResult] = useState(null);
  const [refused, setRefused] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const asked = useRef(false);

  useEffect(() => {
    if (!visible || asked.current) return;
    asked.current = true;
    Promise.all([api.get('/api/v1/costing/rates'), api.get('/api/v1/costing/prices')])
      .then(([r, p]) => { setRates(r.data); setMats(p.data.materials || []); })
      .catch(err => setError(say(err, 'The current figures could not be read just now. Reload the page and try again.')));
  }, [visible]);

  if (error) return <p style={{ ...note, color: '#fca5a5' }}>{error}</p>;
  if (!rates) return <p style={note}>Reading today's figures…</p>;
  const s = rates.settings;
  const people = (s.people || []).map((p, i) => ({ ...p, i })).filter(p => p.pay.kind !== 'minimum_wage');
  const matName = (id) => (mats.find(m => String(m.id) === String(id)) || {}).name || 'A material';
  const today = (k) => (k === 'electricity_per_kwh' ? shown(rates.electricity_per_kwh === null ? null : Math.round(rates.electricity_per_kwh * 1000) / 1000) : shown(s[k]));
  const typed = (v) => String(v ?? '').trim() !== '';
  const clear = () => { setForm({}); setPay({}); setOwn([]); setPick(''); setPickPrice(''); setResult(null); setRefused(null); setMissingOpen(false); };
  const add = () => {
    if (!pick || !typed(pickPrice)) return;
    setOwn(list => [...list.filter(o => o.id !== pick), { id: pick, price: pickPrice }]);
    setPick(''); setPickPrice('');
  };
  const work = async () => {
    const changes = {};
    for (const [k] of [...CHANGES, ...PERCENT_CHANGES]) if (typed(form[k])) changes[k] = form[k];
    const pays = Object.fromEntries(Object.entries(pay).filter(([, v]) => typed(v)));
    if (Object.keys(pays).length) changes.people = pays;
    if (own.length) changes.materials = Object.fromEntries(own.map(o => [o.id, o.price]));
    setBusy(true); setRefused(null);
    try {
      const r = await api.post('/api/v1/costing/what-if', { changes });
      setResult(r.data);
      setMissingOpen(false);
    } catch (err) {
      setResult(null);
      setRefused(say(err, 'It could not be worked out just now. Try again.'));
    } finally { setBusy(false); }
  };

  const row = (key, label, hint, placeholder, value, onChange) => (
    <label key={key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 104px', gap: 10, alignItems: 'center' }}>
      <span style={{ fontSize: 13 }}>{label}<span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>{hint}</span></span>
      <input aria-label={label} inputMode="decimal" placeholder={placeholder} style={input('100%')} value={value ?? ''} onChange={e => onChange(e.target.value)} />
    </label>
  );
  const stat = (label, value, sub) => (
    <div style={{ background: SUNK, borderRadius: 8, padding: '10px 13px', display: 'grid', gap: 1, alignContent: 'start' }}>
      <div style={{ fontSize: 12, color: DIM }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: MUTED }}>{sub}</div>}
    </div>
  );
  const pair = (c) => <>{c.now} → <span style={{ color: c.then > c.now ? AMBER : TEXT }}>{c.then}</span></>;
  const small = (text) => <span style={{ display: 'block', fontSize: 11, color: MUTED }}>{text}</span>;
  const moved = (r) => money(r.then.cost, 4) !== money(r.now.cost, 4) || percent(r.then.margin) !== percent(r.now.margin);
  const nameCell = (r) => <td style={phone ? td : { ...td, minWidth: 200 }}>{r.name}{r.number && <span style={{ display: 'block', fontSize: 11, color: MUTED }}>{r.number}</span>}</td>;
  const done = result ? result.rows.filter(r => r.complete) : [];
  const missing = result ? result.rows.filter(r => !r.complete) : [];
  const machinesMoved = result ? result.machines.filter(m => m.now !== m.then) : [];

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <section style={box}>
        <h2 style={h2}>What to change</h2>
        <p style={note}>Nothing here is saved. To keep a change, type it on the Rates or Prices tab. Leave a box empty for no change.</p>
        <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: '10px 28px' }}>
          {CHANGES.map(([k, label, hint]) => row(k, label, hint, today(k), form[k], v => setForm(f => ({ ...f, [k]: v }))))}
          {people.map(p => row(`pay${p.i}`, `${p.name}, ${p.pay.kind === 'year' ? 'a year' : 'net a month'}`, p.pay.kind === 'year' ? '€ a year' : '€ a month', shown(p.pay.amount), pay[p.i], v => setPay(m => ({ ...m, [p.i]: v }))))}
          {PERCENT_CHANGES.map(([k, label, hint]) => row(k, label, hint, '0', form[k], v => setForm(f => ({ ...f, [k]: v }))))}
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 13 }}>One material<span style={{ display: 'block', fontSize: 11.5, color: MUTED }}>Its own price, instead of any change in % above</span></span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <select aria-label="Material" value={pick} onChange={e => { setPick(e.target.value); const m = mats.find(x => String(x.id) === e.target.value); setPickPrice(m && m.price !== null ? shown(m.price) : ''); }}
              style={{ ...input(phone ? '100%' : 260, false), textAlign: 'left' }}>
              <option value="">Choose a material…</option>
              {mats.map(m => <option key={m.id} value={m.id}>{m.name}{m.price === null ? ' (no price)' : ` (€ ${money(m.price, 2)} a kg)`}</option>)}
            </select>
            <input aria-label="Price a kg" inputMode="decimal" placeholder="€ a kg" style={input(96)} value={pickPrice} onChange={e => setPickPrice(e.target.value)} />
            <button type="button" style={quiet} onClick={add}>+ Add</button>
          </div>
          {own.map(o => {
            const m = mats.find(x => String(x.id) === String(o.id));
            return (
              <div key={o.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, minWidth: phone ? 0 : 200 }}>{matName(o.id)} <span style={{ color: MUTED, fontSize: 11.5 }}>{m && m.price !== null ? `today € ${money(m.price, 2)} a kg` : 'no price today'}</span></span>
                <input aria-label={`${matName(o.id)}, € a kg`} inputMode="decimal" style={input(96)} value={o.price} onChange={e => setOwn(list => list.map(x => (x.id === o.id ? { ...x, price: e.target.value } : x)))} />
                <button type="button" aria-label={`Remove ${matName(o.id)}`} style={{ ...quiet, padding: '3px 9px' }} onClick={() => setOwn(list => list.filter(x => x.id !== o.id))}>×</button>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button type="button" style={primary(!busy)} disabled={busy} onClick={work}>{busy ? 'Working it out…' : 'Work it out'}</button>
          <button type="button" style={quiet} onClick={clear}>Clear</button>
          {refused && <span role="alert" style={{ fontSize: 13, color: '#fca5a5', flex: '1 1 260px' }}>{refused}</span>}
        </div>
      </section>

      {result && (
        <>
          <section style={box}>
            <h2 style={h2}>If</h2>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 3, fontSize: 13.5 }}>
              {result.said.map(t => <li key={t}>{t}</li>)}
            </ul>
            {result.notes.map(t => <p key={t} style={{ ...note, color: AMBER }}>{t}</p>)}
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 10 }}>
            {stat('Complete products', result.summary.complete, `${result.summary.priced} with a list price`)}
            {stat('Cost up', result.summary.cost_up, `${result.summary.cost_down} down`)}
            {stat('Below minimum', pair({ now: result.summary.below_minimum.now, then: result.summary.below_minimum.then }), 'Now → after')}
            {stat('Below variable cost', pair({ now: result.summary.below_variable.now, then: result.summary.below_variable.then }), 'Now → after')}
          </div>

          {machinesMoved.length > 0 && (
            <section style={box}>
              <h2 style={h2}>An hour on each machine</h2>
              <div style={scroll}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr><th style={th()}>Machine</th><th style={th(true)}>Now</th><th style={th(true)}>After</th></tr></thead>
                  <tbody>
                    {machinesMoved.map(m => (
                      <tr key={m.name}><td style={td}>{m.name}</td><td style={tdr}>{m.now === null ? '—' : `€ ${money(m.now, 2)}`}</td><td style={tdr}>{m.then === null ? '—' : `€ ${money(m.then, 2)}`}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section style={box}>
            <h2 style={h2}>Complete products, biggest drop in margin first</h2>
            <div style={scroll}>
              <table style={phone ? { borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: 544 } : { borderCollapse: 'collapse', width: '100%', minWidth: 760 }}>
                {phone && <colgroup><col style={{ width: 130 }} /><col style={{ width: 92 }} /><col style={{ width: 92 }} /><col style={{ width: 110 }} /><col style={{ width: 120 }} /></colgroup>}
                <thead>
                  {phone
                    ? <tr><th style={th()}>Product or part</th><th style={th(true)}>Cost</th><th style={th(true)}>Margin</th><th style={th(true)}>Price for target margin</th><th style={th()}>Standing after</th></tr>
                    : <tr><th style={th()}>Product or part</th><th style={th(true)}>Cost now</th><th style={th(true)}>Cost after</th><th style={th(true)}>Margin now</th><th style={th(true)}>Margin after</th><th style={th(true)}>Price for target margin</th><th style={th()}>Standing after</th></tr>}
                </thead>
                <tbody>
                  {done.map(r => {
                    const dc = r.then.cost - r.now.cost, dm = r.then.margin === null || r.now.margin === null ? 0 : r.then.margin - r.now.margin;
                    const dash = <span style={{ color: MUTED }}>—</span>;
                    const costChange = money(dc, 4) !== money(0, 4) && small(signed(dc, 4));
                    const marginChange = r.then.price !== null && money(dm * 100, 1) !== money(0, 1) && small(`${signed(dm * 100, 1)} %`);
                    return (
                      <tr key={`${r.kind}-${r.id}`} style={{ opacity: moved(r) ? 1 : 0.6 }}>
                        {nameCell(r)}
                        {phone ? (
                          <>
                            <td style={tdr}>{money(r.then.cost, 4)}{small(`was ${money(r.now.cost, 4)}`)}</td>
                            <td style={tdr}>{r.then.price === null ? dash : percent(r.then.margin)}{r.then.price !== null && r.now.price !== null ? small(`was ${percent(r.now.margin)}`) : null}</td>
                          </>
                        ) : (
                          <>
                            <td style={tdr}>{money(r.now.cost, 4)}</td>
                            <td style={tdr}>{money(r.then.cost, 4)}{costChange}</td>
                            <td style={tdr}>{r.now.price === null ? dash : percent(r.now.margin)}</td>
                            <td style={tdr}>{r.then.price === null ? dash : percent(r.then.margin)}{marginChange}</td>
                          </>
                        )}
                        <td style={tdr}>{r.then.target_price === null ? dash : money(r.then.target_price, 4)}</td>
                        <td style={td}>{r.then.price === null ? <span style={{ color: MUTED }}>none</span> : standingChip({ complete: true, standing: r.then.standing })}</td>
                      </tr>
                    );
                  })}
                  {!done.length && <tr><td colSpan={phone ? 5 : 7} style={{ ...td, color: DIM }}>No product is complete yet. Fill in what is missing on the Prices tab.</td></tr>}
                </tbody>
              </table>
            </div>
            <p style={note}>"none" means no list price, so there is no margin to show. {result.payment && result.payment.says}</p>
          </section>

          {missing.length > 0 && (
            <section style={box}>
              <button type="button" aria-expanded={missingOpen} onClick={() => setMissingOpen(o => !o)}
                style={{ background: 'none', border: 'none', padding: 0, color: TEXT, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className={`fa-solid fa-chevron-${missingOpen ? 'down' : 'right'}`} style={{ fontSize: 10, color: DIM, width: 10 }} />
                <h2 style={h2}>{missing.length} with something missing</h2>
              </button>
              {missingOpen && (
                <>
                  <p style={note}>These costs are not complete, so they read as "at least" and their margins are not shown. Fill them in on the Prices tab.</p>
                  <div style={scroll}>
                    <table style={phone ? { borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: 324 } : { borderCollapse: 'collapse', width: '100%', minWidth: 560 }}>
                      {phone && <colgroup><col style={{ width: 130 }} /><col style={{ width: 110 }} /><col style={{ width: 84 }} /></colgroup>}
                      <thead>
                        {phone
                          ? <tr><th style={th()}>Product or part</th><th style={th(true)}>Cost</th><th style={th()}>Missing</th></tr>
                          : <tr><th style={th()}>Product or part</th><th style={th(true)}>Cost now</th><th style={th(true)}>Cost after</th><th style={th()}>Missing</th></tr>}
                      </thead>
                      <tbody>
                        {missing.map(r => (
                          <tr key={`${r.kind}-${r.id}`}>
                            {nameCell(r)}
                            {phone ? (
                              <td style={tdr}>{costWord({ cost: r.then.cost, complete: false })}{small(`was ${costWord({ cost: r.now.cost, complete: false })}`)}</td>
                            ) : (
                              <>
                                <td style={tdr}>{costWord({ cost: r.now.cost, complete: false })}</td>
                                <td style={tdr}>{costWord({ cost: r.then.cost, complete: false })}</td>
                              </>
                            )}
                            <td style={td}>{chip(`${r.gap_count} missing`, SUNK, AMBER)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default function CostPrice() {
  const goBack = useBack('/admin');
  const phone = useWindowWidth() < 760;
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'products';
  const openKey = params.get('open');
  const [kind, id] = openKey ? openKey.split(':') : [null, null];
  const setTab = useCallback((t, item) => setParams(t === 'products' ? {} : (item ? { tab: t, item } : { tab: t })), [setParams]);
  const [open, setOpen] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const count = useCallback((n) => setOpen(n), []);
  const pill = (id, label) => (
    <button type="button" onClick={() => setTab(id)} aria-pressed={tab === id} style={{ background: tab === id ? TEAL : 'none', color: tab === id ? '#06221f' : DIM, border: `1px solid ${tab === id ? TEAL : BORD}`, borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
  );
  return (
    <div style={{ padding: phone ? 14 : 22, maxWidth: 1180, margin: '0 auto', color: TEXT, fontFamily: 'system-ui, sans-serif', display: 'grid', gap: 14 }}>
      <div>
        <button type="button" onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 6, fontFamily: 'inherit' }}>‹ Back</button>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 3px' }}>Cost &amp; Price</h1>
        <p style={{ ...note, fontSize: 13 }}>What each product costs and earns, built from what the app already knows. Rates and Prices are where the money comes from.</p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {pill('products', 'Products')}
        {pill('rates', 'Rates')}
        {pill('prices', open ? `Prices · ${open} to do` : 'Prices')}
        {pill('whatif', 'What if')}
      </div>
      <div style={{ display: tab === 'products' && !openKey ? 'block' : 'none' }}>
        <ProductList visible={tab === 'products' && !openKey} refresh={refresh} phone={phone} onTab={setTab} onOpen={(k, i) => setParams({ open: `${k}:${i}` })} />
      </div>
      {tab === 'products' && openKey && (
        <ProductPage key={openKey} kind={kind} id={id} phone={phone} onBack={() => setParams({})} onTab={setTab} onChanged={() => setRefresh(n => n + 1)} />
      )}
      <div style={{ display: tab === 'rates' ? 'block' : 'none' }}><Rates phone={phone} /></div>
      <div style={{ display: tab === 'prices' ? 'block' : 'none' }}><Prices onCount={count} focus={params.get('item')} refresh={refresh} /></div>
      <div style={{ display: tab === 'whatif' ? 'block' : 'none' }}><WhatIf visible={tab === 'whatif'} phone={phone} /></div>
    </div>
  );
}
